import { useQuery } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";

export interface Contractor {
  id: number;
  name: string;
  email: string;
  phone: string;
  taxId: string;
  status: string;
  businessId: number;
  address: {
    street: string;
    city: string;
    state: string;
    zipCode: string;
    country: string;
  };
  createdAt: string;
  updatedAt: string;
}

export function useContractors(businessId: number) {
  return useQuery<Contractor[], Error>({
    queryKey: ["contractors", businessId],
    queryFn: async () => {
      if (!businessId) return [];
      
      try {
        // Use regular fetch to avoid the API issues
        const response = await fetch(`/api/businesses/${businessId}/contractors`);
        if (!response.ok) {
          // Return empty array if 4xx status (like 402 subscription required)
          if (response.status >= 400 && response.status < 500) {
            return [];
          }
          throw new Error(`Failed to fetch contractors: ${response.status}`);
        }
        const data = await response.json() as Contractor[];
        return data || [];
      } catch (error) {
        console.error("Error fetching contractors:", error);
        return [];
      }
    },
    enabled: !!businessId,
  });
}

export function useContractor(businessId: number, contractorId: number) {
  return useQuery<Contractor | null, Error>({
    queryKey: ["contractor", businessId, contractorId],
    queryFn: async () => {
      if (!businessId || !contractorId) return null;
      
      try {
        // Use regular fetch to avoid the API issues
        const response = await fetch(`/api/businesses/${businessId}/contractors/${contractorId}`);
        if (!response.ok) {
          // Return null if 4xx status (like 402 subscription required)
          if (response.status >= 400 && response.status < 500) {
            return null;
          }
          throw new Error(`Failed to fetch contractor: ${response.status}`);
        }
        const data = await response.json() as Contractor;
        return data;
      } catch (error) {
        console.error("Error fetching contractor:", error);
        return null;
      }
    },
    enabled: !!businessId && !!contractorId,
  });
}
